/**
 * Safely format a FastAPI error response for display in a toast.
 *
 * FastAPI returns 422 validation errors as:
 *   { detail: [ { type, loc, msg, input, ... }, ... ] }
 *
 * Calling `toast.error(err.detail)` on this directly throws:
 *   "Objects are not valid as a React child (found: object with keys {type, loc, msg, input})"
 * which crashes the form. This helper normalizes the payload into a readable string.
 *
 * Usage:
 *   const err = await res.json().catch(() => ({}));
 *   toast.error(formatApiError(err));
 */
export function formatApiError(err, fallback = 'Save failed') {
  if (!err) return fallback;
  // FastAPI's HTTPException → { detail: "string" }
  if (typeof err.detail === 'string') return err.detail;
  // FastAPI 422 → { detail: [ {msg, loc, ...}, ... ] }
  if (Array.isArray(err.detail)) {
    return err.detail.map(e => e?.msg || JSON.stringify(e)).join('; ') || fallback;
  }
  // Custom backend errors → { detail: { message: "..." } }
  if (err.detail && typeof err.detail === 'object') {
    return err.detail.message || err.detail.error || JSON.stringify(err.detail);
  }
  // Generic { message: "..." }
  if (typeof err.message === 'string') return err.message;
  return fallback;
}
